/**
 * Care Readiness Dashboard (demo placeholder)
 *
 * A static, non-interactive panel that visually represents the coordination
 * steps our AI agents (and occasional human overrides) drive before a Hospital
 * at Home admit visit. It is intentionally hardcoded — it illustrates the kind
 * of orchestration we want to surface for autoscheduled patients rather than
 * reflecting live data. Rendered from the intake queue for autoscheduled rows.
 *
 * Its chrome is shared with the Discharge Readiness Dashboard; see
 * ReadinessDashboardShell.
 */

import AirIcon from "@mui/icons-material/Air";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import MedicationIcon from "@mui/icons-material/Medication";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";

import ReadinessDashboardShell, {
  AI_CALL,
  AI_PORTAL,
  AI_SMS,
  CoordinationTask,
  HUMAN_OVERRIDE,
} from "./ReadinessDashboardShell";

// The six coordination steps from the mockup, in reading order. Hardcoded on
// purpose — this is an illustrative placeholder.
const TASKS: CoordinationTask[] = [
  {
    icon: MedicationIcon,
    title: "Medications Coordinated",
    subtitle: "Pharmacy confirmed & delivered to home",
    channel: AI_CALL,
    complete: true,
    timing: "Completed 5:40 AM",
  },
  {
    icon: Inventory2Icon,
    title: "Supplies Coordinated",
    subtitle: "Clinical supply kit packed & staged",
    channel: AI_PORTAL,
    complete: true,
    timing: "Completed 5:52 AM",
  },
  {
    icon: LocalShippingIcon,
    title: "Transport from Hospital",
    subtitle: "Discharge transport confirmed",
    channel: HUMAN_OVERRIDE,
    complete: true,
    timing: "Completed 6:05 AM",
  },
  {
    icon: MedicalServicesIcon,
    title: "DME Delivered & Set Up",
    subtitle: "Durable medical equipment on-site",
    channel: AI_SMS,
    complete: false,
    timing: "Due by 6:30 AM",
    declined: "rollator walker",
  },
  {
    icon: MonitorHeartIcon,
    title: "RPM Installed & Verified",
    subtitle: "Remote monitoring live & transmitting",
    channel: AI_CALL,
    complete: false,
    timing: "Due by 7:00 AM",
  },
  {
    icon: AirIcon,
    title: "Oxygen Delivered",
    subtitle: "Home O2 concentrator set up & tested",
    channel: AI_SMS,
    complete: false,
    timing: "Due by 7:45 AM",
  },
];

export type CareReadinessDashboardProps = {
  patientName: string;
  programLabel: string;
  admitVisit?: string;
};

export const CareReadinessDashboard = ({
  patientName,
  programLabel,
  admitVisit = "Today, 8:00 AM",
}: CareReadinessDashboardProps) => (
  <ReadinessDashboardShell
    title={`${programLabel} · Care Readiness Dashboard`}
    meta={`Patient: ${patientName} · Admit Visit: ${admitVisit}`}
    tasks={TASKS}
    footerText={(complete, total) =>
      `${complete} of ${total} complete — remaining steps on track for ${admitVisit.replace(/^Today, /, "")} admit visit`
    }
  />
);

export default CareReadinessDashboard;
